import { AgentApprovalState } from '@/ApiClient';

// What a tool call was, in the user's words rather than the tool's. The person reading a
// transcript did not choose these names and should not have to learn them.
//
// Past tense throughout: a transcript is a record of what happened. The one exception is the
// write, which is the only call that can sit unanswered — see `writeLabels`.
const labels: Record<string, string> = {
  search_api: 'Looked up the API',
  list_brands: 'Listed brands',
  get_brand_context: 'Read the brand configuration',
  read: 'Read from the PLM',
  dry_run: 'Rehearsed a change',
  run: 'Made a change',
  request_upload: 'Prepared an upload',
  check_upload: 'Checked an upload',
  report_gap: 'Reported a gap',
  python: 'Ran a calculation',
  bash: 'Ran a command',
  read_file: 'Read a file',
  write: 'Wrote a file',
  edit: 'Edited a file',
  glob: 'Looked for files',
  grep: 'Searched files',
};

/**
 * A write before the user has answered it, and after they refused it. `run` is the only tool
 * the agent needs permission for, so it is the only row that is ever read while the thing it
 * describes has not happened — and, once denied, never will.
 */
const writeLabels: Partial<Record<AgentApprovalState, string>> = {
  [AgentApprovalState.Pending]: 'Wants to make a change',
  [AgentApprovalState.Denied]: 'Change denied',
};

/**
 * `toolServer` says the call went to the PLM rather than to the sandbox, which is what tells a
 * `read` of the catalogue from a `read` of a file — the two share a name and mean different
 * things.
 */
export function toolLabel(
  toolName: string | undefined,
  toolServer: string | undefined | null,
  approvalState?: AgentApprovalState | null
): string {
  const name = toolName ?? '';
  if (toolServer) {
    if (name === 'run' && approvalState != null && writeLabels[approvalState]) return writeLabels[approvalState];
    return labels[name] ?? `Called ${name}`;
  }
  // Before the shared map, not after it: `read` has an entry there, and reaching it would
  // label a file the sandbox opened as a read of the catalogue.
  if (name === 'read') return 'Read a file';
  return labels[name] ?? `Used ${name}`;
}

/**
 * What a run of calls did, for a line summarising it collapsed: each distinct activity once, in
 * the order it first happened. Repeats are dropped rather than counted — an agent that reads
 * eleven times did one thing eleven times, and the count is already on the row.
 */
export function toolLabelSummary(
  calls: { toolName?: string; toolServer?: string | null; approvalState?: AgentApprovalState | null }[]
): string {
  const seen: string[] = [];
  for (const call of calls) {
    const label = toolLabel(call.toolName, call.toolServer, call.approvalState);
    if (!seen.includes(label)) seen.push(label);
  }
  return seen.join(' · ');
}
